import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { sessionService } from '../services/SessionService';
import { efemService } from '../services/EfemService';
import { useEfemLockEvents } from '../hooks/useEfemLockEvents';
import { useSubdomain } from '../contexts/SubdomainContext';
import {
  isEfemLockOwnedByCurrentUser,
  readCurrentEfemLockUser,
} from '../utils/efemLockOwnership';

interface EfemLockState {
  gatewaySerialNumber: string | null;
  clientName: string | null;
  expiresAt: Date | null;
  operation: string | null;
}

interface EfemLockContextType {
  globalLock: EfemLockState | null;
  setGlobalLock: (lock: EfemLockState | null) => void;
  hasActiveLock: () => boolean;
  checkForExistingLocks: () => Promise<void>;
  clearGlobalLock: () => void;
}

const EfemLockContext = createContext<EfemLockContextType | undefined>(undefined);

const STORAGE_KEY = 'efem_global_lock';

export const EfemLockProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [globalLock, setGlobalLockState] = useState<EfemLockState | null>(null);
  const { isActive, isLoading: subscriptionLoading } = useSubdomain();
  const efemRealtimeEnabled =
    sessionService.isAuthenticated() && !subscriptionLoading && isActive;

  // Set lock and persist to localStorage
  const setGlobalLock = useCallback((lock: EfemLockState | null) => {    
    setGlobalLockState(lock);
    if (lock) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(lock));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  // Clear lock
  const clearGlobalLock = useCallback(() => {
    setGlobalLock(null);
  }, [setGlobalLock]);

  // Check if user has any active lock
  const hasActiveLock = useCallback(() => {
    return globalLock !== null;
  }, [globalLock]);

  // Check backend for existing locks on mount/refresh
  const checkForExistingLocks = useCallback(async () => {
    if (!efemRealtimeEnabled) {
      return;
    }

    try {
      // Use the dedicated API that returns all clients with lock status
      const response = await efemService.getEfemClientsWithLocks();
      
      if (!response.success || !response.data?.clients) {
        return;
      }

      // Find client locked by current user
      const currentUser = readCurrentEfemLockUser();
      const lockedClient = response.data.clients.find((client) => {
        if (!client.lockStatus?.isLocked) {
          return false;
        }
        return isEfemLockOwnedByCurrentUser(client.lockStatus, currentUser);
      });

      if (lockedClient && lockedClient.lockStatus) {
        // Found an active lock held by current user
        console.log('Restored existing lock:', lockedClient);
        
        setGlobalLock({
          gatewaySerialNumber: lockedClient.gatewaySerialNumber,
          clientName: lockedClient.name,
          expiresAt: new Date(lockedClient.lockStatus.expiresAt!),
          operation: lockedClient.lockStatus.operation || 'unknown',
        });
      } else {
        // No active locks found - clear any stale localStorage data
        clearGlobalLock();
      }
    } catch (error) {
      console.error('Failed to check for existing locks:', error);
    }
  }, [setGlobalLock, clearGlobalLock, efemRealtimeEnabled]);

  useEfemLockEvents({
    enabled: efemRealtimeEnabled,
    onLockEvent: () => {
      void checkForExistingLocks();
    },
  });

  // Load from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const lock = JSON.parse(stored);
        // Convert expiresAt string back to Date
        if (lock.expiresAt) {
          lock.expiresAt = new Date(lock.expiresAt);
        }
        setGlobalLockState(lock);
      } catch (error) {
        console.error('Failed to parse stored lock:', error);
        localStorage.removeItem(STORAGE_KEY);
      }
    }
    
    // Check backend for actual lock state
    if (efemRealtimeEnabled) {
      void checkForExistingLocks();
    }
  }, [checkForExistingLocks, efemRealtimeEnabled]);

  // Sync across tabs using storage events
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        if (e.newValue) {
          try {
            const lock = JSON.parse(e.newValue);
            if (lock.expiresAt) {
              lock.expiresAt = new Date(lock.expiresAt);
            }
            setGlobalLockState(lock);
          } catch (error) {
            console.error('Failed to parse lock from storage event:', error);
          }
        } else {
          setGlobalLockState(null);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  return (
    <EfemLockContext.Provider
      value={{
        globalLock,
        setGlobalLock,
        hasActiveLock,
        checkForExistingLocks,
        clearGlobalLock,
      }}
    >
      {children}
    </EfemLockContext.Provider>
  );
};

export const useEfemLockContext = () => {
  const context = useContext(EfemLockContext);
  if (!context) {
    throw new Error('useEfemLockContext must be used within EfemLockProvider');
  }
  return context;
};
